import { useEffect } from 'react';
import { OnboardingFormFields } from '@Types/index';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { Country } from '@Types/country';
import type { IHomeAddressForm } from '@Types/homeAddress';

interface SelectedCountryParams {
  selectedCountry?: Country | null;
  country: string | undefined;
  state: string;
  postalCode: string;
  city: string;
  resetField: (
    field: keyof IHomeAddressForm,
    options: { defaultValue: IHomeAddressForm[keyof IHomeAddressForm] }
  ) => void;
  clearErrors: (fields: (keyof IHomeAddressForm)[]) => void;
  setValue: (
    field: keyof IHomeAddressForm,
    value: IHomeAddressForm[keyof IHomeAddressForm]
  ) => void;
  formMethods: ExtendedUseFormReturn<IHomeAddressForm>;
}

export const useSelectedCountryEffects = ({
  selectedCountry,
  country,
  state,
  postalCode,
  city,
  resetField,
  clearErrors,
  setValue,
  formMethods,
}: SelectedCountryParams) => {
  useEffect(() => {
    if (!selectedCountry) return;

    clearErrors([OnboardingFormFields.STATE, OnboardingFormFields.ZIP]);

    // Check if selected country matches stored customer data
    const shouldAutoPopulate = selectedCountry.code === country && country;

    if (shouldAutoPopulate) {
      // Auto-populate with stored customer data
      const matchingState = selectedCountry.subDivisions?.find(
        (subDivision) => subDivision.code === state
      );

      if (matchingState) {
        const stateDropdownItem = {
          title: matchingState.name,
          value: matchingState.code,
        };
        setValue(OnboardingFormFields.STATE, stateDropdownItem);
      } else {
        resetField(OnboardingFormFields.STATE, { defaultValue: { title: '', value: '' } });
      }

      setValue(OnboardingFormFields.ZIP, postalCode);
      formMethods.trigger();
    } else {
      // Clear all address fields when country doesn't match stored data
      resetField(OnboardingFormFields.ZIP, { defaultValue: '' });
      resetField(OnboardingFormFields.STATE, { defaultValue: { title: '', value: '' } });
    }
  }, [selectedCountry, country, state, postalCode, city, resetField, clearErrors, setValue]);
};
